import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hospice_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Only logout on 401 if it's not already a retry and not a login/register request
    if (error.response?.status === 401 && !originalRequest._retry) {
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                            originalRequest.url?.includes('/auth/register');
      
      if (!isAuthEndpoint) {
        // Try to refresh the user session first
        const token = localStorage.getItem("hospice_token");
        if (token) {
          originalRequest._retry = true;
          try {
            // Attempt to get user data again - if token is still valid
            const response = await api.get("/auth/me");
            if (response.data) {
              // Token is still valid, retry original request
              return api(originalRequest);
            }
          } catch (refreshError) {
            // Token is truly invalid, logout
            localStorage.removeItem("hospice_token");
            localStorage.removeItem("hospice_user");
            localStorage.removeItem("hospice_session_id");
            window.dispatchEvent(new Event("auth-logout"));
          }
        } else {
          // No token, trigger logout
          window.dispatchEvent(new Event("auth-logout"));
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = async (email, password, name, role = "caregiver", securityQuestion = null, securityAnswer = null) => {
  const response = await api.post("/auth/register", { 
    email, password, name, role, 
    security_question: securityQuestion,
    security_answer: securityAnswer
  });
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post("/auth/login", { email, password });
  return response.data;
};

export const getMe = async () => {
  const response = await api.get("/auth/me");
  return response.data;
};

export const updateDiseaseCategory = async (diseaseCategory) => {
  const response = await api.patch(`/auth/me/disease?disease_category=${diseaseCategory}`);
  return response.data;
};

// Security Questions & Password Reset
export const getSecurityQuestions = async () => {
  const response = await api.get("/auth/security-questions");
  return response.data;
};

export const setSecurityQuestion = async (securityQuestion, securityAnswer) => {
  const response = await api.post("/auth/security-question", {
    security_question: securityQuestion,
    security_answer: securityAnswer
  });
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await api.post("/auth/password-reset/request", { email });
  return response.data;
};

export const verifyAndResetPassword = async (email, securityAnswer, newPassword) => {
  const response = await api.post("/auth/password-reset/verify", {
    email,
    security_answer: securityAnswer,
    new_password: newPassword
  });
  return response.data;
};

// Diseases
export const getDiseaseCategories = async () => {
  const response = await api.get("/diseases");
  return response.data;
};

// Chat with retry logic for reliability
export const sendChatMessage = async (message, sessionId, context = "goals_of_care", diseaseCategory = null, retries = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await api.post("/chat", { 
        message, 
        session_id: sessionId, 
        context, 
        disease_category: diseaseCategory 
      }, {
        timeout: 60000 // 60 second timeout for AI responses
      });
      return response.data;
    } catch (error) {
      lastError = error;
      console.warn(`Chat attempt ${attempt}/${retries} failed:`, error.message);
      if (attempt < retries) {
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }
  throw lastError;
};

export const getChatHistory = async (sessionId, context = null) => {
  const params = context ? { context } : {};
  const response = await api.get(`/chat/history/${sessionId}`, { params });
  return response.data;
};

export const getUserConversations = async () => {
  const response = await api.get("/chat/conversations");
  return response.data;
};

export const clearChatHistory = async (sessionId) => {
  const response = await api.delete(`/chat/history/${sessionId}`);
  return response.data;
};

// Visit Questions
export const getVisitQuestions = async () => {
  const response = await api.get("/visit-questions");
  return response.data;
};

export const getVisitQuestionsByDisease = async (diseaseCategory) => {
  const response = await api.get(`/visit-questions/by-disease/${diseaseCategory}`);
  return response.data;
};

// Contact
export const submitContact = async (data) => {
  const response = await api.post("/contact", data);
  return response.data;
};

export const getContactMessages = async () => {
  const response = await api.get("/contact");
  return response.data;
};

export const updateContactStatus = async (messageId, status) => {
  const response = await api.patch(`/contact/${messageId}?status=${status}`);
  return response.data;
};

// Analytics
export const recordPageVisit = async (page, userAgent = null) => {
  const response = await api.post("/analytics/visit", { page, user_agent: userAgent });
  return response.data;
};

export const getAnalyticsSummary = async () => {
  const response = await api.get("/analytics/summary");
  return response.data;
};

// Symptoms
export const getSymptomGuides = async () => {
  const response = await api.get("/symptoms");
  return response.data;
};

export const getSymptomGuide = async (symptomName) => {
  const response = await api.get(`/symptoms/${symptomName}`);
  return response.data;
};

export const downloadSymptomPDF = async (symptomName) => {
  const response = await api.get(`/symptoms/${symptomName}/pdf`, { responseType: 'blob' });
  return response.data;
};

export const downloadAllSymptomsPDF = async () => {
  const response = await api.get("/symptoms/export/all", { responseType: 'blob' });
  return response.data;
};

// Journal
export const createJournalEntry = async (entryData) => {
  const response = await api.post("/journal", entryData);
  return response.data;
};

export const getJournalEntries = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get("/journal", { params });
  return response.data;
};

export const getJournalEntry = async (entryId) => {
  const response = await api.get(`/journal/${entryId}`);
  return response.data;
};

export const deleteJournalEntry = async (entryId) => {
  const response = await api.delete(`/journal/${entryId}`);
  return response.data;
};

export const getWeeklyJournalSummary = async () => {
  const response = await api.get("/journal/summary/weekly");
  return response.data;
};

// Videos
export const getVideos = async (category = null) => {
  const params = category ? { category } : {};
  const response = await api.get("/videos", { params });
  return response.data;
};

export const createVideo = async (videoData) => {
  const response = await api.post("/videos", videoData);
  return response.data;
};

export const deleteVideo = async (videoId) => {
  const response = await api.delete(`/videos/${videoId}`);
  return response.data;
};

// Messages
export const createConversation = async (subject, message) => {
  const response = await api.post("/messages/conversations", { subject, message });
  return response.data;
};

export const getConversations = async () => {
  const response = await api.get("/messages/conversations");
  return response.data;
};

export const getConversationMessages = async (conversationId) => {
  const response = await api.get(`/messages/conversations/${conversationId}`);
  return response.data;
};

export const sendMessage = async (conversationId, content) => {
  const response = await api.post(`/messages/conversations/${conversationId}`, { content });
  return response.data;
};

// Provider
export const getProviderPatients = async () => {
  const response = await api.get("/provider/patients");
  return response.data;
};

export const getPatientJournal = async (patientId) => {
  const response = await api.get(`/provider/patients/${patientId}/journal`);
  return response.data;
};

export const getProviderInsights = async () => {
  const response = await api.get("/provider/insights");
  return response.data;
};

// Community Resources
export const getResources = async (category = null, search = null) => {
  const params = {};
  if (category) params.category = category;
  if (search) params.search = search;
  const response = await api.get("/resources", { params });
  return response.data;
};

export const getResource = async (resourceId) => {
  const response = await api.get(`/resources/${resourceId}`);
  return response.data;
};

export const createResource = async (resourceData) => {
  const response = await api.post("/resources", resourceData);
  return response.data;
};

export const updateResource = async (resourceId, resourceData) => {
  const response = await api.put(`/resources/${resourceId}`, resourceData);
  return response.data;
};

export const deleteResource = async (resourceId) => {
  const response = await api.delete(`/resources/${resourceId}`);
  return response.data;
};

// Joyful Memories
export const createMemory = async (memoryData) => {
  const response = await api.post("/memories", memoryData);
  return response.data;
};

export const getMemories = async (includePublic = true, memoryType = null) => {
  const params = { include_public: includePublic };
  if (memoryType) params.memory_type = memoryType;
  const response = await api.get("/memories", { params });
  return response.data;
};

export const getMemory = async (memoryId) => {
  const response = await api.get(`/memories/${memoryId}`);
  return response.data;
};

export const updateMemory = async (memoryId, memoryData) => {
  const response = await api.put(`/memories/${memoryId}`, memoryData);
  return response.data;
};

export const deleteMemory = async (memoryId) => {
  const response = await api.delete(`/memories/${memoryId}`);
  return response.data;
};

export const likeMemory = async (memoryId) => {
  const response = await api.post(`/memories/${memoryId}/like`);
  return response.data;
};

export const aiEnhanceMemory = async (content) => {
  const response = await api.post("/memories/ai-enhance", null, { params: { content } });
  return response.data;
};

// Staff Dashboard
export const getStaffDashboard = async (timeframe = "week", startDate = null, endDate = null) => {
  const params = { timeframe };
  if (timeframe === "custom" && startDate && endDate) {
    params.start_date = startDate;
    params.end_date = endDate;
  }
  const response = await api.get("/staff/dashboard", { params });
  return response.data;
};

// Theme Preferences
export const getAvailableThemes = async () => {
  const response = await api.get("/themes");
  return response.data;
};

export const getThemePreferences = async () => {
  const response = await api.get("/auth/me/theme");
  return response.data;
};

export const updateThemePreferences = async (colorScheme = null, fontSize = null, highContrast = null, reduceAnimations = null, darkMode = null) => {
  const params = {};
  if (colorScheme !== null) params.color_scheme = colorScheme;
  if (fontSize !== null) params.font_size = fontSize;
  if (highContrast !== null) params.high_contrast = highContrast;
  if (reduceAnimations !== null) params.reduce_animations = reduceAnimations;
  if (darkMode !== null) params.dark_mode = darkMode;
  const response = await api.put("/auth/me/theme", null, { params });
  return response.data;
};

// Audit Logs
export const getAuditLogs = async (startDate = null, endDate = null, action = null, userId = null, limit = 100) => {
  const params = { limit };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (action) params.action = action;
  if (userId) params.user_id = userId;
  const response = await api.get("/audit-logs", { params });
  return response.data;
};

// ==================== HIPAA COMPLIANCE APIs ====================

// Session Management
export const getSessionStatus = async () => {
  const response = await api.get("/session/status");
  return response.data;
};

export const extendSession = async () => {
  const response = await api.post("/session/extend");
  return response.data;
};

export const logoutSession = async () => {
  const response = await api.post("/session/logout");
  return response.data;
};

// Two-Factor Authentication
export const get2FAStatus = async () => {
  const response = await api.get("/auth/2fa/status");
  return response.data;
};

export const setup2FA = async () => {
  const response = await api.post("/auth/2fa/setup");
  return response.data;
};

export const enable2FA = async (code) => {
  const response = await api.post("/auth/2fa/enable", { code });
  return response.data;
};

export const verify2FA = async (code) => {
  const response = await api.post("/auth/2fa/verify", { code });
  return response.data;
};

export const disable2FA = async (code) => {
  const response = await api.post("/auth/2fa/disable", { code });
  return response.data;
};

// Patient-Staff Assignments
export const assignPatient = async (patientId, staffId, assignmentType = "primary", notes = null) => {
  const response = await api.post("/assignments", {
    patient_id: patientId,
    staff_id: staffId,
    assignment_type: assignmentType,
    notes
  });
  return response.data;
};

export const getAssignments = async () => {
  const response = await api.get("/assignments");
  return response.data;
};

export const getMyPatients = async () => {
  const response = await api.get("/assignments/my-patients");
  return response.data;
};

export const getMyCareTeam = async () => {
  const response = await api.get("/assignments/my-care-team");
  return response.data;
};

export const removeAssignment = async (assignmentId) => {
  const response = await api.delete(`/assignments/${assignmentId}`);
  return response.data;
};

// Emergency Access
export const requestEmergencyAccess = async (reason, urgency, patientId = null) => {
  const response = await api.post("/emergency-access/request", {
    reason,
    urgency,
    patient_id: patientId
  });
  return response.data;
};

export const getActiveEmergencySessions = async () => {
  const response = await api.get("/emergency-access/active");
  return response.data;
};

export const getEmergencyAccessLogs = async (includeExpired = false, limit = 100) => {
  const response = await api.get("/emergency-access/logs", { params: { include_expired: includeExpired, limit } });
  return response.data;
};

export const revokeEmergencyAccess = async (sessionId) => {
  const response = await api.post(`/emergency-access/${sessionId}/revoke`);
  return response.data;
};

export const getMyEmergencySession = async () => {
  const response = await api.get("/emergency-access/my-session");
  return response.data;
};

// Pending AI Reviews
export const getPendingReviews = async (priority = null, reviewType = null) => {
  const params = {};
  if (priority) params.priority = priority;
  if (reviewType) params.review_type = reviewType;
  const response = await api.get("/reviews/pending", { params });
  return response.data;
};

export const submitReviewDecision = async (reviewId, decision, notes = null) => {
  const response = await api.post(`/reviews/${reviewId}/decision`, { decision, notes });
  return response.data;
};

// Compliance Status
export const getComplianceStatus = async () => {
  const response = await api.get("/compliance/status");
  return response.data;
};

export const getAdminComplianceOverview = async () => {
  const response = await api.get("/compliance/admin-overview");
  return response.data;
};

// ==================== STAFF CASELOAD MANAGEMENT ====================

export const getStaffCaseload = async () => {
  const response = await api.get("/staff/caseload");
  return response.data;
};

export const getStaffPatientDetails = async (patientId) => {
  const response = await api.get(`/staff/caseload/patient/${patientId}`);
  return response.data;
};

export const importCaseload = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post("/staff/caseload/import", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const getCaseloadTemplate = async () => {
  const response = await api.get("/staff/caseload/export-template");
  return response.data;
};

export const assignStaffPatient = async (staffId, patientId, assignmentType = "primary", notes = null) => {
  const response = await api.post("/staff/caseload/assign", {
    staff_id: staffId,
    patient_id: patientId,
    assignment_type: assignmentType,
    notes
  });
  return response.data;
};

export const removeStaffAssignment = async (assignmentId) => {
  const response = await api.delete(`/staff/caseload/assign/${assignmentId}`);
  return response.data;
};

export const getStaffList = async () => {
  const response = await api.get("/staff/list");
  return response.data;
};

export const getAllPatientsList = async () => {
  const response = await api.get("/staff/patients/list");
  return response.data;
};

export const createPatientManually = async (patientData) => {
  const response = await api.post("/staff/patients/create", patientData);
  return response.data;
};

export const getAllPatientsWithSearch = async (search = "", limit = 50, offset = 0) => {
  const response = await api.get("/staff/patients/all", {
    params: { search, limit, offset }
  });
  return response.data;
};

// ==================== STAFF-PATIENT MESSAGING ====================

export const createStaffConversation = async (patientId, subject, initialMessage) => {
  const response = await api.post("/staff/messages/conversation", {
    patient_id: patientId,
    subject,
    initial_message: initialMessage
  });
  return response.data;
};

export const getStaffConversations = async () => {
  const response = await api.get("/staff/messages/conversations");
  return response.data;
};

export const getStaffConversationMessages = async (conversationId) => {
  const response = await api.get(`/staff/messages/conversation/${conversationId}`);
  return response.data;
};

export const sendStaffMessage = async (conversationId, content) => {
  const response = await api.post(`/staff/messages/conversation/${conversationId}`, { content });
  return response.data;
};

export const getStaffUnreadCount = async () => {
  const response = await api.get("/staff/messages/unread-count");
  return response.data;
};

// ==================== STAFF APPRECIATION ====================

export const sendStaffAppreciation = async (staffId, rating, message, category = "general", patientId = null, isPublic = true) => {
  const response = await api.post("/staff/appreciation", {
    staff_id: staffId,
    rating,
    message,
    category,
    patient_id: patientId,
    is_public: isPublic
  });
  return response.data;
};

export const getStaffAppreciations = async (staffId) => {
  const response = await api.get(`/staff/appreciation/${staffId}`);
  return response.data;
};

export const getAppreciationLeaderboard = async () => {
  const response = await api.get("/staff/appreciation/leaderboard");
  return response.data;
};

// ==================== MOOD TRENDS ====================

export const getCaseloadMoodTrends = async (days = 30) => {
  const response = await api.get(`/staff/caseload/mood-trends?days=${days}`);
  return response.data;
};

export const getPatientMoodHistory = async (patientId, days = 90) => {
  const response = await api.get(`/staff/caseload/patient/${patientId}/mood-history?days=${days}`);
  return response.data;
};

// ==================== SMS NOTIFICATION SETTINGS ====================

export const getSmsSettings = async () => {
  const response = await api.get("/staff/settings/sms");
  return response.data;
};

export const updateSmsSettings = async (phone, smsNotifications) => {
  const response = await api.put("/staff/settings/sms", {
    phone,
    sms_notifications: smsNotifications
  });
  return response.data;
};

export const getSmsNotificationLogs = async (limit = 20) => {
  const response = await api.get(`/staff/notifications/sms-logs?limit=${limit}`);
  return response.data;
};

// ==================== PARTNER DOCUMENTS ====================

export const uploadPartnerDocument = async (patientId, file, description = null, category = "general") => {
  const formData = new FormData();
  formData.append('patient_id', patientId);
  formData.append('file', file);
  if (description) formData.append('description', description);
  formData.append('category', category);
  
  const response = await api.post("/partners/documents/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

export const getPartnerPatientDocuments = async (patientId) => {
  const response = await api.get(`/partners/documents/patient/${patientId}`);
  return response.data;
};

export const downloadPartnerDocument = async (documentId) => {
  const response = await api.get(`/partners/documents/${documentId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

export const viewPartnerDocument = async (documentId) => {
  const response = await api.get(`/partners/documents/${documentId}/view`);
  return response.data;
};

export const deletePartnerDocument = async (documentId) => {
  const response = await api.delete(`/partners/documents/${documentId}`);
  return response.data;
};

export const getDocumentCategories = async () => {
  const response = await api.get("/partners/documents/categories");
  return response.data;
};

// ==================== ACADEMY PROGRESS ====================

export const getAcademyProgress = async () => {
  const response = await api.get("/academy/progress");
  return response.data;
};

export const syncAcademyProgress = async (progressData) => {
  const response = await api.post("/academy/progress/sync", progressData);
  return response.data;
};

export const completeCourse = async (courseId, quizScore = null, quizPassed = false, ceuCredits = 0) => {
  const response = await api.post("/academy/progress/complete-course", {
    course_id: courseId,
    quiz_score: quizScore,
    quiz_passed: quizPassed,
    ceu_credits: ceuCredits
  });
  return response.data;
};

export const recordQuizAttempt = async (courseId, score, passed, answers = null) => {
  const response = await api.post("/academy/progress/quiz-attempt", {
    course_id: courseId,
    score,
    passed,
    answers
  });
  return response.data;
};

export const markVideoWatched = async (courseId) => {
  const response = await api.post("/academy/progress/video-watched", {
    course_id: courseId
  });
  return response.data;
};

export const addAcademyAchievement = async (achievementId) => {
  const response = await api.post(`/academy/progress/achievement?achievement_id=${achievementId}`);
  return response.data;
};

export const getAcademyStats = async () => {
  const response = await api.get("/academy/progress/stats");
  return response.data;
};

export const getAcademyLeaderboard = async (limit = 20) => {
  const response = await api.get(`/academy/admin/leaderboard?limit=${limit}`);
  return response.data;
};

// ==================== ADMIN APPROVALS ====================

export const getApprovalDashboard = async () => {
  const response = await api.get("/admin/approvals/dashboard");
  return response.data;
};

export const getPendingApprovals = async (category = null, limit = 50) => {
  const params = new URLSearchParams();
  if (category) params.append("category", category);
  params.append("limit", limit);
  const response = await api.get(`/admin/approvals/pending?${params}`);
  return response.data;
};

export const getUserDetailsForApproval = async (userId) => {
  const response = await api.get(`/admin/approvals/user/${userId}`);
  return response.data;
};

export const approveUser = async (userId) => {
  const response = await api.post(`/admin/approvals/user/${userId}/approve`);
  return response.data;
};

export const rejectUser = async (userId, reason = null) => {
  const response = await api.post(`/admin/approvals/user/${userId}/reject`, {
    action: "reject",
    reason
  });
  return response.data;
};

export const bulkApprovalAction = async (userIds, action, reason = null) => {
  const response = await api.post("/admin/approvals/bulk", {
    user_ids: userIds,
    action,
    reason
  });
  return response.data;
};

export const getApprovedUsers = async (category = null, limit = 50) => {
  const params = new URLSearchParams();
  if (category) params.append("category", category);
  params.append("limit", limit);
  const response = await api.get(`/admin/approvals/approved?${params}`);
  return response.data;
};

export const getRejectedUsers = async (limit = 50) => {
  const response = await api.get(`/admin/approvals/rejected?limit=${limit}`);
  return response.data;
};

// ==================== PATIENT/CAREGIVER MESSAGING ====================

export const getPatientConversations = async () => {
  const response = await api.get("/staff/patient/messages/conversations");
  return response.data;
};

export const getPatientConversationMessages = async (conversationId) => {
  const response = await api.get(`/staff/patient/messages/conversation/${conversationId}`);
  return response.data;
};

export const sendPatientMessage = async (conversationId, content) => {
  const response = await api.post(`/staff/messages/conversation/${conversationId}`, { content });
  return response.data;
};

export const getPatientUnreadCount = async () => {
  const response = await api.get("/staff/messages/unread-count");
  return response.data;
};

export const createPatientConversation = async (subject, initialMessage) => {
  const response = await api.post("/staff/patient/messages/conversation", {
    subject,
    initial_message: initialMessage
  });
  return response.data;
};

export const getPatientCareTeam = async () => {
  const response = await api.get("/staff/patient/care-team");
  return response.data;
};

// Read Receipts
export const markPatientMessagesRead = async (conversationId) => {
  const response = await api.post(`/staff/patient/messages/conversation/${conversationId}/read`);
  return response.data;
};

export const markStaffMessagesRead = async (conversationId) => {
  const response = await api.post(`/staff/messages/conversation/${conversationId}/read`);
  return response.data;
};

// Typing Indicators
export const setPatientTyping = async (conversationId, isTyping) => {
  const response = await api.post(`/staff/patient/messages/conversation/${conversationId}/typing`, { is_typing: isTyping });
  return response.data;
};

export const setStaffTyping = async (conversationId, isTyping) => {
  const response = await api.post(`/staff/messages/conversation/${conversationId}/typing`, { is_typing: isTyping });
  return response.data;
};

export const getPatientTypingStatus = async (conversationId) => {
  const response = await api.get(`/staff/patient/messages/conversation/${conversationId}/typing-status`);
  return response.data;
};

export const getStaffTypingStatus = async (conversationId) => {
  const response = await api.get(`/staff/messages/conversation/${conversationId}/typing-status`);
  return response.data;
};

// ==================== PUSH NOTIFICATIONS ====================

export const getVapidKey = async () => {
  const response = await api.get("/push/vapid-key");
  return response.data;
};

export const subscribeToPush = async (subscription) => {
  const response = await api.post("/push/subscribe", subscription);
  return response.data;
};

export const unsubscribeFromPush = async (endpoint) => {
  const response = await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`);
  return response.data;
};

export const getUserNotifications = async (limit = 50, unreadOnly = false) => {
  const response = await api.get(`/push/notifications?limit=${limit}&unread_only=${unreadOnly}`);
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await api.post(`/push/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.post("/push/notifications/read-all");
  return response.data;
};

// ==================== CALL TRIAGE ====================

export const addToTriageQueue = async (callData) => {
  const response = await api.post("/triage/queue/add", callData);
  return response.data;
};

export const getTriageQueue = async () => {
  const response = await api.get("/triage/queue");
  return response.data;
};

export const getTriageStats = async () => {
  const response = await api.get("/triage/stats");
  return response.data;
};

export const getTriageHistory = async (limit = 50) => {
  const response = await api.get(`/triage/history?limit=${limit}`);
  return response.data;
};

// ==================== ADMIN ACTIVITY DASHBOARD ====================

export const getAllPatientActivities = async (sortBy = "created_at", sortOrder = "desc", limit = 100, activityType = null) => {
  const params = new URLSearchParams();
  params.append("sort_by", sortBy);
  params.append("sort_order", sortOrder);
  params.append("limit", limit);
  if (activityType) params.append("activity_type", activityType);
  const response = await api.get(`/admin/activities?${params}`);
  return response.data;
};

export const getPatientActivityDetails = async (patientId, days = 30) => {
  const response = await api.get(`/admin/activities/patient/${patientId}?days=${days}`);
  return response.data;
};

// ==================== CLINICAL INFO ALERTS ====================

export const getClinicalAlerts = async () => {
  const response = await api.get("/staff/clinical-alerts");
  return response.data;
};

export const dismissClinicalAlert = async (alertId) => {
  const response = await api.post(`/staff/clinical-alerts/${alertId}/dismiss`);
  return response.data;
};

// ==================== PATIENT/CAREGIVER SCHEDULING ====================

export const getMyScheduledVisits = async (includePast = false, daysAhead = 30) => {
  const response = await api.get("/scheduling/my-visits", {
    params: { include_past: includePast, days_ahead: daysAhead }
  });
  return response.data;
};

export const getNextScheduledVisit = async () => {
  const response = await api.get("/scheduling/my-visits/next");
  return response.data;
};

// ==================== PATIENT/CAREGIVER TRIAGE CALL REQUESTS ====================

export const requestCallback = async (reason, priority = "normal", callbackNumber = null, preferredTime = null, notes = null) => {
  const response = await api.post("/triage/portal/request-call", {
    reason,
    priority,
    callback_number: callbackNumber,
    preferred_time: preferredTime,
    notes
  });
  return response.data;
};

export const getMyCallRequests = async () => {
  const response = await api.get("/triage/portal/my-requests");
  return response.data;
};

export const getQueueStatus = async () => {
  const response = await api.get("/triage/portal/queue-status");
  return response.data;
};

// ==================== NOTIFICATION PREFERENCES ====================

export const getNotificationPreferences = async () => {
  const response = await api.get("/push/preferences");
  return response.data;
};

export const updateNotificationPreferences = async (preferences) => {
  const response = await api.put("/push/preferences", preferences);
  return response.data;
};

// ==================== FHIR PHI DATA (Google Cloud Healthcare API) ====================

/**
 * Check FHIR store connection status
 */
export const checkFHIRHealth = async () => {
  const response = await api.get("/fhir/health");
  return response.data;
};

/**
 * Create a new patient in FHIR store (PHI)
 * @param {Object} patientData - Patient demographics
 * @param {string} patientData.given_name - First name
 * @param {string} patientData.family_name - Last name
 * @param {string} patientData.birth_date - YYYY-MM-DD format
 * @param {string} patientData.gender - male | female | other | unknown
 * @param {string} [patientData.telephone] - Phone number
 * @param {string} [patientData.email] - Email address
 * @param {Object} [patientData.address] - Address object
 */
export const createFHIRPatient = async (patientData) => {
  const response = await api.post("/fhir/patients", patientData);
  return response.data;
};

/**
 * Get a patient from FHIR store by ID
 * @param {string} patientId - FHIR Patient ID
 */
export const getFHIRPatient = async (patientId) => {
  const response = await api.get(`/fhir/patients/${patientId}`);
  return response.data;
};

/**
 * Update a patient in FHIR store
 * @param {string} patientId - FHIR Patient ID
 * @param {Object} patientData - Updated patient data
 */
export const updateFHIRPatient = async (patientId, patientData) => {
  const response = await api.put(`/fhir/patients/${patientId}`, patientData);
  return response.data;
};

/**
 * Delete a patient from FHIR store
 * @param {string} patientId - FHIR Patient ID
 */
export const deleteFHIRPatient = async (patientId) => {
  const response = await api.delete(`/fhir/patients/${patientId}`);
  return response.data;
};

/**
 * Search patients in FHIR store
 * @param {Object} params - Search parameters
 * @param {string} [params.family_name] - Last name
 * @param {string} [params.given_name] - First name
 * @param {string} [params.birthdate] - Birth date YYYY-MM-DD
 */
export const searchFHIRPatients = async (params = {}) => {
  const response = await api.get("/fhir/patients", { params });
  return response.data;
};

/**
 * Get all resources related to a patient
 * @param {string} patientId - FHIR Patient ID
 */
export const getFHIRPatientEverything = async (patientId) => {
  const response = await api.get(`/fhir/patients/${patientId}/everything`);
  return response.data;
};

/**
 * Create an observation (vitals, pain score, etc.) in FHIR store
 * @param {Object} observationData
 * @param {string} observationData.patient_id - FHIR Patient ID
 * @param {string} observationData.code - LOINC code
 * @param {string} observationData.code_display - Human-readable name
 * @param {any} observationData.value - Observed value
 * @param {string} [observationData.unit] - Unit of measurement
 * @param {string} [observationData.category] - vital-signs | laboratory
 */
export const createFHIRObservation = async (observationData) => {
  const response = await api.post("/fhir/observations", observationData);
  return response.data;
};

/**
 * Get observations for a patient
 * @param {string} patientId - FHIR Patient ID
 * @param {string} [category] - Filter by category
 */
export const getFHIRPatientObservations = async (patientId, category = null) => {
  const params = category ? { category } : {};
  const response = await api.get(`/fhir/observations/patient/${patientId}`, { params });
  return response.data;
};

/**
 * Create a condition (diagnosis) in FHIR store
 * @param {Object} conditionData
 * @param {string} conditionData.patient_id - FHIR Patient ID
 * @param {string} conditionData.code - ICD-10 code
 * @param {string} conditionData.code_display - Diagnosis name
 */
export const createFHIRCondition = async (conditionData) => {
  const response = await api.post("/fhir/conditions", conditionData);
  return response.data;
};

/**
 * Create a medication request in FHIR store
 * @param {Object} medicationData
 * @param {string} medicationData.patient_id - FHIR Patient ID
 * @param {string} medicationData.medication_code - RxNorm code
 * @param {string} medicationData.medication_display - Medication name
 * @param {string} medicationData.dosage_instruction - Dosage instructions
 */
export const createFHIRMedicationRequest = async (medicationData) => {
  const response = await api.post("/fhir/medication-requests", medicationData);
  return response.data;
};

/**
 * Create a care plan in FHIR store
 * @param {Object} carePlanData
 * @param {string} carePlanData.patient_id - FHIR Patient ID
 * @param {string} carePlanData.title - Care plan title
 * @param {string} carePlanData.description - Care plan description
 */
export const createFHIRCarePlan = async (carePlanData) => {
  const response = await api.post("/fhir/care-plans", carePlanData);
  return response.data;
};

/**
 * Create an encounter (visit) in FHIR store
 * @param {Object} encounterData
 * @param {string} encounterData.patient_id - FHIR Patient ID
 * @param {string} encounterData.encounter_type - Type of visit
 * @param {string} [encounterData.start_time] - Visit start time
 * @param {string} [encounterData.end_time] - Visit end time
 * @param {string} [encounterData.reason] - Reason for visit
 */
export const createFHIREncounter = async (encounterData) => {
  const response = await api.post("/fhir/encounters", encounterData);
  return response.data;
};

export default api;
